/**
 * The contract every external system plugs into.
 *
 * A provider module is the only thing in the app that knows what "Gmail" means:
 * which scopes it needs, what its natural subcollections are, how to ask it for
 * a date range, and how to turn what comes back into something the timeline can
 * render. Everything downstream sees only HGTimeRecordPlus.
 *
 * The server deliberately holds no copy of this. It stores `provider_key` and
 * `provider_config` as opaque strings, because a server-side catalogue would be
 * a second list of providers that is always one deploy behind this one.
 */

import { ComponentType } from "react";

import { HGRecordUrl } from "@/common/types";

export type ProviderConfig = Record<string, unknown>;

/** One item as the provider sees it, before it becomes a record. */
export interface ExternalItem {
  /** Stable within the provider; forms part of the synthetic record id. */
  externalId: string;
  title: string;
  content: string;
  /** When it happened. */
  at: Date;
  /**
   * The end of a span -- a ticket open→closed, a trip. Omit for a point event
   * like an email, which then occupies a single second.
   */
  until?: Date;
  url?: HGRecordUrl[];
  /**
   * The permalink, when the system hands one over with the item (Calendar's
   * `htmlLink`, and every issue tracker's `html_url`). Providers that have to
   * build it out of the id implement `itemUrl` instead; this wins when both
   * are present, because a link the system printed cannot be out of date.
   */
  href?: string;
}

/** What `fetchDetail` needs: one item, not a range. */
export interface ProviderDetailContext {
  accessToken: string;
  config: ProviderConfig;
  externalId: string;
  signal: AbortSignal;
}

export interface ProviderFetchContext {
  accessToken: string;
  config: ProviderConfig;
  from: Date;
  to: Date;
  limit: number;
  signal: AbortSignal;
}

export interface ProviderChildTemplate {
  title: string;
  providerKey: string;
  config: ProviderConfig;
}

export interface ProviderLimits {
  /**
   * Nothing this provider knows about happened before here. Ranges entirely
   * outside it are never requested at all -- a Gmail collection viewed at the
   * Jurassic costs no network.
   */
  earliest?: Date;
  /**
   * Wider than this and the source is not read at all: the timeline shows a
   * "zoom in" hint in its place. The bound is about how much a person can read,
   * not what the API can answer -- a month of mail is already four hundred
   * rows, while a year of somebody's calendar is a few hundred at most.
   *
   * `loadExternalSource` words the hint from this number, so a new cap needs
   * no new copy as long as `spanPhrase` there can name it.
   */
  maxSpanDays: number;
  /** Hard cap on items in one fetch; the UI says when it was hit. */
  maxItems: number;
}

export interface ExternalProviderModule {
  /** Stored in collection.provider_key. */
  key: string;
  /** Which connection it fetches with; several providers may share one. */
  systemId: string;
  title: string;
  /** One line, shown in the create-collection library. */
  description: string;
  Icon: ComponentType<{ className?: string }>;
  scopes: string[];
  /**
   * Created as real collection rows alongside the root. Real rows rather than
   * virtual ones so the collection graph stays uniform -- the sidebar, pins,
   * slugs and traversals need no special case.
   */
  naturalChildren: ProviderChildTemplate[];
  limits: ProviderLimits;
  /**
   * Where the fetch runs. Only "client" is implemented: Google's APIs, GitHub
   * and GitLab all send CORS headers, so the browser can call them directly and
   * no user data touches our server. Atlassian's gateway does not, so the field
   * exists to give a future server-side proxy one place to branch rather than a
   * redesign.
   */
  transport?: "client" | "proxy";
  fetchItems(ctx: ProviderFetchContext): Promise<ExternalItem[]>;
  /**
   * The item's whole text, fetched only when one is actually opened.
   *
   * `fetchItems` deliberately returns an excerpt: the list needs two lines per
   * row and a hundred full message bodies would be a hundred times the payload
   * for text nobody has asked to read. This is the second half of that trade,
   * and it is optional -- a provider whose list rows are already complete need
   * not implement it.
   */
  fetchDetail?(ctx: ProviderDetailContext): Promise<string>;
  /**
   * Permalink into the source system, built from the item. External records
   * are read-only here, so this is the only way out to the real thing.
   *
   * Optional: a provider whose API already returns the link sets `href` on
   * each item instead.
   */
  itemUrl?(item: ExternalItem, config: ProviderConfig): string | undefined;
}

/** Thrown when the grant is gone; surfaces as a "reconnect" prompt, not an error. */
export class ExternalAuthError extends Error {
  constructor(message = "This connection needs to be authorized again.") {
    super(message);
    this.name = "ExternalAuthError";
  }
}
